/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useEffect, useState } from 'react';
import { Box, makeStyles, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { IsInRole } from 'shared/authorization/IsInRole';
import { Roles } from 'shared/enums/Roles';
import { RoutePath } from 'shared/enums/RoutePath';
import { INITIAL_PAGE_SORTING_AND_FILTERING } from 'pages/userTable/components/useUserTable';
import UserMenuProfile from '../../../../layout/menu/UserMenuProfile';
import { AccountType } from '../../../../shared/enums/AccountType';
import { SwitchAccountUserRoles } from '../../../userAccounts/components/SwitchAccountUserRoles';
import RoleSelector from '../../../../components/user/roleSelector';
import useRemoveUserFromOrg from '../../../../redux/features/user/remove/useRemoveUserFromOrg';
import RemoveUserDialog from '../../../../components/user/removeUserDialog';
import { useUsers } from '../../../../redux/features/users/paginate/useUsers';
import DeleteUserInOrg from './DeleteUserInOrg';

const useStyles = makeStyles(theme => ({
  secondaryProfile: {
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(3),
  },
  roleChips: {
    display: 'flex',
    flexWrap: 'wrap',
    listStyle: 'none',
    padding: theme.spacing(0),
    margin: 0,
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      display: 'grid',
      gap: '10%',
      gridTemplateColumns: '1fr 1fr',
    },
  },
  orgTitle: {
    fontSize: '1.2rem',
    fontWeight: '400',
    color: theme.palette.lightBorder,
    marginBottom: theme.spacing(1),
  },
}));

function UserOrganizationInfo({
  editMode,
  userRoles,
  onRoleChange,
  showActionButton,
  editRole,
  onClick,
  userId,
  currentOrganization,
}) {
  const [showProfileLinkIcon, setShowProfileLinkIcon] = useState(false);
  const classes = useStyles();
  const { t } = useTranslation();

  const organizationId = currentOrganization?.id;
  const { getUsers } = useUsers(INITIAL_PAGE_SORTING_AND_FILTERING, organizationId);

  const { removeDialog, onToggleRemoveDialog, onRemoveUserConfirmInOrg } = useRemoveUserFromOrg(
    currentOrganization?.id,
    getUsers,
  );

  useEffect(() => {
    if (currentOrganization?.type === AccountType.ORGANIZATION) {
      setShowProfileLinkIcon(true);
    }
  }, [currentOrganization]);

  if (!currentOrganization) return null;

  const to = RoutePath.generateRoutePath(RoutePath.USER_SEARCH, {
    organization: organizationId,
  });

  return (
    <>
      <Box className={classes.secondaryProfile}>
        <UserMenuProfile
          className={classes.orgTitle}
          user={t(currentOrganization?.type)}
          userLink={currentOrganization?.type === AccountType.ORGANIZATION ? currentOrganization?.name : null}
          showIcon={showProfileLinkIcon}
          to={to}
        />
      </Box>
      <Box>
        <Typography variant="h6" component="h6" gutterBottom>
          {t('user.roles')}
        </Typography>
        {editMode ? (
          <RoleSelector roles={userRoles} onRoleChange={onRoleChange} editRole={editRole} />
        ) : (
          <SwitchAccountUserRoles className={classes.roleChips} roles={userRoles} />
        )}
      </Box>
      <Box>
        {/* // TODO: Work on organizationAccentColor later, comment the component should be temporary, must have it in FO 1.xv */}
        {!showActionButton ? null : ( // <OrganizationAccentColorPicker />
          <IsInRole roles={[Roles.END_USER_ADMIN]}>
            <DeleteUserInOrg onClick={onClick} />
          </IsInRole>
        )}
        {removeDialog.open && (
          <RemoveUserDialog
            userId={userId}
            dialog={removeDialog}
            toggleDialog={onToggleRemoveDialog}
            onSubmit={onRemoveUserConfirmInOrg}
          />
        )}
      </Box>
    </>
  );
}

export default UserOrganizationInfo;
